/* ==========================================================================
   everaw-chrome.js — V19
   ==========================================================================

   The navigation drawer controller.

   Depends on everaw-gesture.js for the spring and the pointer tracker. Loads
   with `defer`, which is safe because the drawer degrades to a plain button +
   panel until this runs, and because nothing here is needed for first paint.

   Design notes worth keeping:

   * The panel is PORTALED to <body> on init. It has to be: rendered in place
     it sits inside the sticky header, which is a stacking context at z-index 3,
     and no z-index on the panel itself can lift it above the chat launcher at
     8000. Moving the node is the only real fix; everything else is a plaster.

   * Position is owned by a single spring, and the spring's value is the single
     source of truth for the panel transform, the scrim opacity, and the chat
     launcher offset. Deriving all three from one number is what keeps them
     locked together when a gesture is interrupted. Three independent
     animations would drift apart the moment anything is grabbed mid-flight.

   * Nothing here uses a CSS transition on transform. A transition cannot be
     interrupted; that is the whole reason this file exists.
   ========================================================================== */

(function (window, document) {
  'use strict';

  var G = window.EverawGesture;
  if (!G) {
    /* everaw-gesture.js failed to load. The markup still works as a static
       panel; better to leave it inert than to half-initialise it. */
    return;
  }

  /* Tap-open spring. damping 1.0 = zero overshoot, which is correct for a
     TAP: a tap carried no momentum, so a bounce would be inventing physics
     that did not happen. Measured against a 335px travel this reaches 90% in
     ~186ms and 99% in ~321ms -- comfortably inside the 150-350ms window where
     UI motion reads as responsive rather than slow. */
  var SPRING = { damping: 1.0, response: 0.3 };

  /* Fraction of the panel width the chat launcher travels before it has fully
     faded. It cannot simply park in the peek strip: the panel is 86vw and the
     launcher is 64px, so on every phone size we ship to there is not enough
     room left for it to remain a legal 44px tap target. So it recedes. */
  var CHAT_TRAVEL_RATIO = 0.55;
  var CHAT_FADE_COMPLETE = 0.7;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------------
     Content offset under the floating nav
     ------------------------------------------------------------------------
     --header-height is NEVER SET on this store. header.liquid:351 returns out
     of connectedCallback for sticky_header_type 'floating' before it reaches
     setHeaderHeight(), so every rule written as
     `calc(var(--header-height, N) + x)` has silently been using its fallback.
     That is why tuning that number kept missing: it was a constant pretending
     to be a measurement.

     Measured properly here instead. Two facts make it simple:

       - .section-header.floating-active is height:0 (sticky-nav-floating:16),
         so <main> begins directly below the announcement bar, which is in flow.
       - The nav pill is position:fixed, so its viewport rect is constant and
         its bottom edge does not move with scroll.

     Needed clearance = pill's bottom edge - announcement bar height + air.
     Recomputed on resize and orientation change, so it stays correct on every
     device rather than being tuned for one.
     ------------------------------------------------------------------------ */
  var CONTENT_AIR = 12;

  function setContentOffset() {
    var pill = document.querySelector('.header-wrapper.ct-header-wrapper');
    if (!pill) return;

    var bar = document.querySelector('.marquee-text');
    var pillBottom = pill.getBoundingClientRect().bottom;
    var barHeight = bar ? bar.getBoundingClientRect().height : 0;

    var offset = Math.max(0, Math.round(pillBottom - barHeight)) + CONTENT_AIR;
    document.documentElement.style.setProperty('--ew-content-offset', offset + 'px');
  }

  function initContentOffset() {
    setContentOffset();
    /* Fonts landing can change the pill's height after first paint. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setContentOffset).catch(function () {});
    }
    window.addEventListener('resize', setContentOffset, { passive: true });
    window.addEventListener('orientationchange', setContentOffset, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContentOffset, { once: true });
  } else {
    initContentOffset();
  }

  /* ------------------------------------------------------------------------
     Shared body scroll lock
     ------------------------------------------------------------------------
     Refcounted and shared by the drawer and the search overlay. If each owned
     its own lock, opening search from the drawer and closing search would
     unlock the page while the drawer was still open, and the stored scroll
     offset of whichever locked first would be lost.

     `overflow: hidden` alone does not hold iOS Safari -- the page still
     rubber-bands behind the overlay, which is what happened before V19.
     position:fixed does hold it, at the cost of jumping to the top, so the
     offset is stored and restored.
     ------------------------------------------------------------------------ */
  var lockCount = 0;
  var lockedScrollY = 0;

  function lockScroll() {
    if (lockCount === 0) {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = '-' + lockedScrollY + 'px';
      document.body.classList.add('ew-nav-locked');
    }
    lockCount += 1;
  }

  function unlockScroll() {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0) return;

    document.body.classList.remove('ew-nav-locked');
    document.body.style.top = '';
    /* 'instant' matters: a smooth restore animates the page back to where it
       already was, which reads as a bug rather than a transition. */
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
  }

  /* ES class, matching the convention already used by global.js
     (`class MenuDrawer extends HTMLElement`). Methods are attached to the
     prototype below rather than declared inline purely to keep each one next
     to its own explanation. */
  class EverawNavDrawer extends HTMLElement {}

  EverawNavDrawer.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.trigger = this.querySelector('.ew-nav__trigger');
    var panelId = this.getAttribute('data-panel-id');
    this.overlay = panelId ? document.getElementById(panelId) : null;

    if (!this.trigger || !this.overlay) return;

    this.panel = this.overlay.querySelector('[data-ew-nav-panel]');
    this.scrim = this.overlay.querySelector('[data-ew-nav-scrim]');
    this.scroller = this.overlay.querySelector('[data-ew-nav-scroll]');
    if (!this.panel || !this.scrim) return;

    /* --- Portal out of the header's stacking context --- */
    if (this.overlay.parentNode !== document.body) {
      document.body.appendChild(this.overlay);
    }

    this.isOpen = false;
    this.width = 0;
    this.scrollY = 0;
    this._chat = null;

    var self = this;
    this.spring = new G.Spring({
      damping: SPRING.damping,
      response: SPRING.response,
      from: 0,
      to: 0,
      onUpdate: function (value) {
        self._render(value);
      },
      onRest: function (value) {
        self._onRest(value);
      }
    });

    this._bind();
    this._initPromo();
  };

  /* ------------------------------------------------------------------------
     Rendering — one number in, three surfaces out.
     `progress` is 0 (closed) to 1 (open).
     ------------------------------------------------------------------------ */
  EverawNavDrawer.prototype._render = function (progress) {
    var w = this.width || this._measure();
    var clamped = progress < 0 ? 0 : progress;

    /* Panel: -100% at 0, 0 at 1. Values above 1 come from rubber-banding past
       the open edge and are allowed through so the resistance is visible. */
    var x = (clamped - 1) * w;
    this.panel.style.transform = 'translate3d(' + x.toFixed(2) + 'px, 0, 0)';

    /* Scrim tracks the panel exactly, so half-open is half-dark. */
    var scrimOpacity = clamped > 1 ? 1 : clamped;
    this.scrim.style.opacity = scrimOpacity.toFixed(3);

    this._renderChat(clamped, w);
  };

  /* The choreography Lucky asked for: the launcher travels with the drawer
     rather than being hidden by it. Driven off the same progress value, so
     during a drag it tracks the finger frame-for-frame. */
  EverawNavDrawer.prototype._renderChat = function (progress, width) {
    var chat = this._chat;
    if (chat === null) {
      chat = this._chat = document.querySelector('.ew-chat') || false;
    }
    if (!chat) return;

    var p = progress > 1 ? 1 : progress;
    var shift = p * width * CHAT_TRAVEL_RATIO;
    var opacity = 1 - p / CHAT_FADE_COMPLETE;
    if (opacity < 0) opacity = 0;
    if (opacity > 1) opacity = 1;
    var scale = 1 - p * 0.25;

    var root = document.documentElement.style;
    root.setProperty('--ew-chat-shift', shift.toFixed(2) + 'px');
    root.setProperty('--ew-chat-opacity', opacity.toFixed(3));
    root.setProperty('--ew-chat-scale', scale.toFixed(3));
  };

  /* ------------------------------------------------------------------------
     Promo carousel
     ------------------------------------------------------------------------
     Entries come from the live announcement bar, read out of the rendered DOM.

     Not from Liquid, and not duplicated into a setting: this snippet is
     rendered by the HEADER section, and Liquid gives a section no access to
     another section's blocks. Reading the marquee that is already on the page
     means the drawer promo and the announcement bar are the same content --
     edit the announcement bar in the theme editor and the drawer follows, with
     nothing to keep in sync.
     ------------------------------------------------------------------------ */
  var PROMO_INTERVAL = 4500;
  var PROMO_SWAP = 220; /* must match the CSS transition */

  EverawNavDrawer.prototype._initPromo = function () {
    this._promoEl = this.overlay.querySelector('[data-ew-nav-promo-text]');
    if (!this._promoEl) return;

    /* The marquee prints every block several times over so it can scroll
       seamlessly, so the same strings repeat. Dedupe, preserving order. */
    var nodes = document.querySelectorAll('.marquee-text_item span:not(.marquee-dot)');
    var seen = Object.create(null);
    var items = [];

    Array.prototype.forEach.call(nodes, function (node) {
      var text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || seen[text]) return;
      seen[text] = true;
      /* Each announcement can carry its own link (data-link, set per block in
         the theme editor). '' when it has none. */
      var holder = node.closest('.marquee-text_item');
      items.push({ text: text, link: (holder && holder.getAttribute('data-link')) || '' });
    });

    /* The card is an <a> only when the header setting gives it a link. That
       link is the default for any message that has none of its own. */
    this._promoA = this._promoEl.closest('a.ew-nav__promo');
    this._promoDefault = this._promoA ? this._promoA.getAttribute('href') : null;

    if (items.length) this._applyPromoLink(items[0]);
    if (items.length < 2) return; /* nothing worth rotating */

    this._promoItems = items;
    this._promoIndex = 0;
    this._promoEl.textContent = items[0].text;
  };

  /* The card follows the visible message: its own link, else the default. */
  EverawNavDrawer.prototype._applyPromoLink = function (item) {
    if (!this._promoA) return;
    this._promoA.setAttribute('href', item.link || this._promoDefault);
  };

  EverawNavDrawer.prototype._startPromo = function () {
    if (!this._promoItems || this._promoTimer) return;
    var self = this;
    this._promoTimer = window.setInterval(function () {
      self._advancePromo();
    }, PROMO_INTERVAL);
  };

  /* Stopped whenever the drawer is closed. An interval ticking against a panel
     nobody can see is pure battery cost, and a looping animation that never
     idles is exactly what the reduced-motion guidance warns about. */
  EverawNavDrawer.prototype._stopPromo = function () {
    if (!this._promoTimer) return;
    window.clearInterval(this._promoTimer);
    this._promoTimer = null;
  };

  EverawNavDrawer.prototype._advancePromo = function () {
    var el = this._promoEl;
    if (!el || !this._promoItems) return;

    var self = this;
    this._promoIndex = (this._promoIndex + 1) % this._promoItems.length;
    var next = this._promoItems[this._promoIndex];

    el.classList.add('is-leaving');
    window.setTimeout(function () {
      el.textContent = next.text;
      self._applyPromoLink(next);
      el.classList.remove('is-leaving');
      el.classList.add('is-entering');
      /* Next frame, so the browser paints the entering offset before it is
         removed -- without this the element simply appears with no transition. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          el.classList.remove('is-entering');
        });
      });
    }, PROMO_SWAP);
  };

  EverawNavDrawer.prototype._measure = function () {
    this.width = this.panel.offsetWidth || Math.min(window.innerWidth * 0.86, 400);
    return this.width;
  };

  EverawNavDrawer.prototype._onRest = function (value) {
    if (value <= 0.001) {
      /* Fully closed: take the overlay out of the accessibility tree and out
         of hit-testing entirely. */
      this.overlay.setAttribute('hidden', '');
      document.documentElement.classList.remove('ew-nav-is-open');
      this._unlockScroll();
      this._releaseFocus();
      this._stopPromo();
      /* Reset to the root view. Reopening into a sub-panel the user had left
         open would be disorienting -- the menu button should always mean "show
         me the menu", not "resume where I was". Done AFTER the panel is off
         screen so the pop is never visible. */
      this.popSub();
    }
  };

  /* Whichever view currently owns the screen. Focus management scopes to this
     rather than the whole panel, otherwise the tab order wraps through links
     inside off-screen sub-panels. */
  EverawNavDrawer.prototype._activeView = function () {
    return this._activeSub || this.overlay.querySelector('[data-ew-nav-root]') || this.panel;
  };

  /* ------------------------------------------------------------------------
     Open / close
     ------------------------------------------------------------------------ */

  EverawNavDrawer.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;

    this.overlay.removeAttribute('hidden');
    /* Force a reflow so the panel is laid out (and measurable) before the
       first spring frame writes a transform to it. */
    this._measure();

    document.documentElement.classList.add('ew-nav-is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    this._lockScroll();
    this._trapFocus();
    this._startPromo();

    this.spring.setTarget(1);
  };

  EverawNavDrawer.prototype.close = function (velocity) {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.trigger.setAttribute('aria-expanded', 'false');
    this.spring.setTarget(0, velocity);
  };

  EverawNavDrawer.prototype.toggle = function () {
    if (this.isOpen) this.close();
    else this.open();
  };

  EverawNavDrawer.prototype._lockScroll = function () {
    if (this._locked) return;
    this._locked = true;
    lockScroll();
  };

  EverawNavDrawer.prototype._unlockScroll = function () {
    if (!this._locked) return;
    this._locked = false;
    unlockScroll();
  };

  /* ------------------------------------------------------------------------
     Focus management
     ------------------------------------------------------------------------ */

  EverawNavDrawer.prototype._trapFocus = function () {
    this._lastFocused = document.activeElement;
    /* Focus the PANEL, not the first focusable child.
       The first focusable is the logo link, so focusing it drew a browser
       focus ring around the logo every single time the drawer opened -- the
       "weird rectangle around the logo" in the V19 preview. Focusing the
       dialog container also matches what screen readers expect: the dialog is
       announced, rather than dropping the user straight onto a Home link. */
    this.panel.focus({ preventScroll: true });
  };

  EverawNavDrawer.prototype._releaseFocus = function () {
    if (this._lastFocused && this._lastFocused.focus) {
      this._lastFocused.focus({ preventScroll: true });
    }
    this._lastFocused = null;
  };

  EverawNavDrawer.prototype._onKeydown = function (event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      /* Escape steps back one level before it closes, matching the way the
         back button and the swipe behave. Closing the whole drawer from inside
         a sub-panel would lose the user's place with no way to recover it. */
      if (this._activeSub) this.popSub();
      else this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    var focusables = Array.prototype.filter.call(
      this._activeView().querySelectorAll(FOCUSABLE),
      function (el) {
        /* offsetParent alone is not enough here: it stays non-null for
           visibility:hidden, which is exactly how off-screen sub-panels are
           hidden. checkVisibility catches both; fall back where unsupported. */
        if (typeof el.checkVisibility === 'function') return el.checkVisibility();
        return el.offsetParent !== null;
      }
    );
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------------------
     Events + drag
     ------------------------------------------------------------------------ */

  EverawNavDrawer.prototype._bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function (event) {
      event.preventDefault();
      self.toggle();
    });

    this.scrim.addEventListener('click', function () {
      self.close();
    });

    var closeBtn = this.overlay.querySelector('[data-ew-nav-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        self.close();
      });
    }

    document.addEventListener('keydown', function (event) {
      self._onKeydown(event);
    });

    /* Any navigation out of the drawer should close it, so returning via the
       back button never lands on a page with a stale open drawer. */
    this.overlay.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href]') : null;
      if (link && !link.hasAttribute('data-ew-nav-close')) self.close();
    });

    this._bindPanels();
    this._bindDrag();

    window.addEventListener(
      'resize',
      function () {
        self.width = 0;
        if (self.isOpen) self._render(1);
      },
      { passive: true }
    );
  };

  /* ------------------------------------------------------------------------
     Sub-panel navigation

     One level deep, deliberately. Deeper stacks need breadcrumbs to stay
     comprehensible, and a four-item menu does not earn that.

     `_activeSub` is the single source of truth: the back button, the Escape
     key, the swipe-back gesture and the drawer's own close all route through
     popSub() rather than each manipulating classes, so the drawer can never end
     up closed with a sub-panel still marked active.
     ------------------------------------------------------------------------ */
  EverawNavDrawer.prototype._bindPanels = function () {
    var self = this;
    this._activeSub = null;

    Array.prototype.forEach.call(
      this.overlay.querySelectorAll('[data-ew-nav-open]'),
      function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          self.pushSub(btn.getAttribute('data-ew-nav-open'), btn);
        });
      }
    );

    Array.prototype.forEach.call(
      this.overlay.querySelectorAll('[data-ew-nav-back]'),
      function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          self.popSub();
        });
      }
    );

    /* Swipe right anywhere on an open sub-panel goes back, matching the
       platform gesture. Bound per panel rather than on the stack so a swipe on
       the root still belongs to the drawer's own close gesture. */
    Array.prototype.forEach.call(
      this.overlay.querySelectorAll('[data-ew-nav-subpanel]'),
      function (panel) {
        new G.DragTracker(panel, {
          axis: 'x',
          threshold: 12,
          canStart: function () {
            return self._activeSub === panel;
          },
          onMove: function (state) {
            /* Only rightward travel moves the panel. Dragging left would be
               asking to go deeper, and there is nowhere deeper to go. */
            if (state.delta <= 0) return;
            var w = self.width || self._measure();
            var x = Math.min(state.delta, w);
            /* Suppress the CSS transition while the finger owns the panel --
               a transition here would lag the finger by its own duration. */
            panel.style.transition = 'none';
            panel.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
          },
          onEnd: function (state) {
            panel.style.transition = '';
            panel.style.transform = '';
            var w = self.width || self._measure();
            var projected = (state.delta + state.projected) / w;
            if (state.velocity > 400 || projected > 0.4) self.popSub();
          }
        });
      }
    );
  };

  EverawNavDrawer.prototype.pushSub = function (id, trigger) {
    var panel = id ? document.getElementById(id) : null;
    if (!panel || this._activeSub === panel) return;

    if (this._activeSub) this._activeSub.classList.remove('is-active');

    panel.classList.add('is-active');
    this.panel.classList.add('has-sub');
    this._activeSub = panel;
    this._subTrigger = trigger || null;

    var back = panel.querySelector('[data-ew-nav-back]');
    if (back) back.focus({ preventScroll: true });
  };

  EverawNavDrawer.prototype.popSub = function () {
    if (!this._activeSub) return;
    this._activeSub.classList.remove('is-active');
    this._activeSub = null;
    this.panel.classList.remove('has-sub');

    /* Return focus to the row that opened it, so the user lands where they
       left rather than at the top of the root list. */
    if (this._subTrigger && this._subTrigger.focus) {
      this._subTrigger.focus({ preventScroll: true });
    }
    this._subTrigger = null;
  };

  EverawNavDrawer.prototype._bindDrag = function () {
    var self = this;

    new G.DragTracker(this.panel, {
      axis: 'x',
      threshold: 8,

      /* Decline the gesture if the finger landed on something that scrolls
         horizontally on its own (the category chips). Two competing horizontal
         gestures on the same surface is the classic way a drawer eats a
         carousel swipe. */
      canStart: function (event) {
        /* A sub-panel is a descendant of this element, so pointerdown on it
           bubbles here too. Without this the drawer's close gesture and the
           sub-panel's back gesture would both claim the same swipe and fight
           over the same finger. Whichever is on top owns it. */
        if (self._activeSub) return false;

        var chips = event.target.closest ? event.target.closest('.ew-nav__chips') : null;
        if (chips) return false;
        return self.isOpen;
      },

      onStart: function () {
        /* Take over from the spring at whatever value it currently holds, so
           grabbing a moving panel does not jump it. */
        self.spring.stop();
        self._dragFrom = self.spring.value;
        document.documentElement.classList.add('ew-nav-is-dragging');
      },

      onMove: function (state) {
        var w = self.width || self._measure();
        var next = self._dragFrom + state.delta / w;

        /* Past fully-open, resist rather than stop dead. */
        if (next > 1) {
          var overshoot = (next - 1) * w;
          next = 1 + G.rubberband(overshoot, w) / w;
        }
        if (next < 0) next = 0;

        self.spring.setValue(next, 0);
        self._render(next);
      },

      onEnd: function (state) {
        document.documentElement.classList.remove('ew-nav-is-dragging');

        var w = self.width || self._measure();
        var current = self.spring.value;

        /* Decide against the PROJECTED resting point, not the release point.
           That is what makes a short fast flick close the drawer while a long
           slow drag that ended near open lets it fall back open. */
        var projectedPx = current * w + state.projected;
        var projected = projectedPx / w;

        /* Velocity sign wins over position when the flick is decisive. */
        var decisive = Math.abs(state.velocity) > 400;
        var shouldClose = decisive ? state.velocity < 0 : projected < 0.5;

        /* Hand the finger's velocity to the spring so there is no visible seam
           between dragging and animating. Spring units are progress/second,
           hence the division by width. */
        var springVelocity = state.velocity / w;

        self.spring.velocity = springVelocity;

        if (shouldClose) {
          self.isOpen = false;
          self.trigger.setAttribute('aria-expanded', 'false');
          self.spring.setTarget(0);
        } else {
          self.isOpen = true;
          self.trigger.setAttribute('aria-expanded', 'true');
          self.spring.setTarget(1);
        }
      }
    });
  };

  if (!window.customElements.get('everaw-nav-drawer')) {
    window.customElements.define('everaw-nav-drawer', EverawNavDrawer);
  }

  /* ========================================================================
     SEARCH OVERLAY
     ========================================================================

     A full-height surface rather than an expand inside the header strip.

     It deliberately does NOT re-implement predictive search. The existing
     <predictive-search> element already cancels in-flight requests with an
     AbortController, caches per term across instances, and implements a full
     roving-option keyboard model. This wraps it and supplies the two things it
     never had: somewhere worth rendering results, and something useful to show
     before the user has typed.
     ======================================================================== */

  var RECENT_KEY = 'everaw:recent-searches';
  var RECENT_MAX = 6;

  /* ------------------------------------------------------------------------
     FAQ answers
     ------------------------------------------------------------------------
     Shopify's search index cannot see the FAQ content: it lives as
     collapsible-content section blocks inside templates/*.json, and the /faqs
     page body is empty. So searching "is it gluten free" returned nothing even
     though that exact answer is published. assets/faq-index.json is compiled
     from those blocks by tools/build_faq_index.py -- 87 unique entries, ~7.7 KB
     gzipped after 111 duplicates are merged.

     PERFORMANCE CONTRACT, which is the whole reason it is built this way:
     the index is NOT in the page payload. It is fetched on pointerdown of the
     search trigger -- on intent, ~100-200ms before the overlay finishes
     animating in -- and cached in sessionStorage. A visitor who never taps
     search pays zero bytes. This matters here more than most places: 90% of
     sessions are mobile and ~78% of those are in-app browsers on Indian mobile
     data (docs/viewport-and-device-targets.md).
     ------------------------------------------------------------------------ */
  var FAQ_CACHE_KEY = 'everaw:faq-index';
  var FAQ_MAX_RESULTS = 3;
  var WORD_RE = /[^a-z0-9]+/g;

  function tokenize(text) {
    return String(text || '')
      .toLowerCase()
      .replace(WORD_RE, ' ')
      .split(' ')
      .filter(function (t) {
        return t.length > 1;
      });
  }

  /* Question words and articles. Without this, "is it gluten free" scored
     "Is it safe for kids?" above the actual gluten-free answer, because `is`
     and `it` each counted as a full question match. They carry no intent. */
  var STOPWORDS = {
    a: 1, an: 1, and: 1, are: 1, at: 1, be: 1, by: 1, can: 1, do: 1, does: 1,
    for: 1, from: 1, has: 1, have: 1, how: 1, in: 1, is: 1, it: 1, its: 1,
    me: 1, my: 1, of: 1, on: 1, or: 1, that: 1, the: 1, there: 1, this: 1,
    to: 1, was: 1, what: 1, when: 1, where: 1, which: 1, will: 1, with: 1,
    you: 1, your: 1
  };

  function contentTokens(tokens) {
    var kept = tokens.filter(function (t) {
      return !STOPWORDS[t];
    });
    /* "how do you" is all stopwords. Better to match on them than to match on
       nothing at all. */
    return kept.length ? kept : tokens;
  }

  /* Word-prefix match, not substring: "gluten" should hit "gluten-free" but
     "ten" should not. Substring matching on a 260-character answer produces
     confident-looking nonsense.

     Exact is scored above prefix, which is what stops "cod" ranking "referral
     code" above "Do you offer Cash on Delivery (COD)?". */
  function matchStrength(haystackTokens, token) {
    var best = 0;
    for (var i = 0; i < haystackTokens.length; i++) {
      var h = haystackTokens[i];
      if (h === token) return 2;
      if (h.indexOf(token) === 0) best = 1;
    }
    return best;
  }

  function scoreEntry(entry, queryTokens) {
    if (!entry._q) {
      entry._q = tokenize(entry.q);
      entry._a = tokenize(entry.a);
    }
    var score = 0;
    var matched = 0;
    for (var i = 0; i < queryTokens.length; i++) {
      var t = queryTokens[i];
      var inQ = matchStrength(entry._q, t);
      if (inQ) {
        /* exact in question = 6, prefix in question = 3 */
        score += inQ * 3;
        matched++;
        continue;
      }
      var inA = matchStrength(entry._a, t);
      if (inA) {
        /* exact in answer = 2, prefix in answer = 1 */
        score += inA;
        matched++;
      }
    }
    /* Every meaningful token must land somewhere. Partial matches are how a
       search for "smoothie shipping" returns an answer about neither. */
    if (matched < queryTokens.length) return 0;
    return score;
  }

  /* Highlight without innerHTML. The query comes from the user, so it is built
     as text nodes plus <mark> elements -- never interpolated into markup. */
  function highlightInto(el, text, queryTokens) {
    el.textContent = '';
    var lower = text.toLowerCase();
    var marks = [];
    queryTokens.forEach(function (t) {
      var from = 0;
      var at;
      while ((at = lower.indexOf(t, from)) !== -1) {
        var before = at === 0 ? ' ' : lower[at - 1];
        if (WORD_RE.test(before) || at === 0) marks.push([at, at + t.length]);
        WORD_RE.lastIndex = 0;
        from = at + t.length;
      }
    });
    if (!marks.length) {
      el.textContent = text;
      return;
    }
    marks.sort(function (a, b) {
      return a[0] - b[0];
    });
    var merged = [marks[0]];
    for (var i = 1; i < marks.length; i++) {
      var last = merged[merged.length - 1];
      if (marks[i][0] <= last[1]) last[1] = Math.max(last[1], marks[i][1]);
      else merged.push(marks[i]);
    }
    var cursor = 0;
    merged.forEach(function (m) {
      if (m[0] > cursor) el.appendChild(document.createTextNode(text.slice(cursor, m[0])));
      var mark = document.createElement('mark');
      mark.textContent = text.slice(m[0], m[1]);
      el.appendChild(mark);
      cursor = m[1];
    });
    if (cursor < text.length) el.appendChild(document.createTextNode(text.slice(cursor)));
  }

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      /* Private mode, or a corrupted value. Recent searches are a nicety, so
         degrade to "none" rather than breaking search entirely. */
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* Storage full or unavailable. Nothing here is worth surfacing. */
    }
  }

  function pushRecent(term) {
    term = (term || '').trim();
    if (term.length < 2) return;
    var list = readRecent().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    writeRecent(list);
  }

  class EverawSearchOverlay extends HTMLElement {}

  EverawSearchOverlay.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.input = this.querySelector('[data-ew-search-input]');
    this.panels = this.querySelector('[data-ew-search-panels]');
    this.recentBlock = this.querySelector('[data-ew-search-recent]');
    this.recentList = this.querySelector('[data-ew-search-recent-list]');
    this.form = this.querySelector('form');

    if (!this.input) return;

    /* Portal for the same reason the drawer is portaled: rendered in place it
       sits inside the sticky header's stacking context and cannot rise above
       the chat launcher. */
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }

    this.answersBlock = this.querySelector('[data-ew-search-answers]');
    this.answerList = this.querySelector('[data-ew-search-answer-list]');
    this.faqUrl = this.getAttribute('data-faq-index') || '';
    this._faq = null;
    this._faqPending = null;

    this.isOpen = false;
    this._bind();
    this._renderRecent();
  };

  /* Fetch once per session. sessionStorage rather than localStorage: the index
     is regenerated on every deploy, and a stale copy pinned across days would
     answer questions with content that is no longer on the site. */
  EverawSearchOverlay.prototype._loadFaq = function () {
    if (this._faq) return Promise.resolve(this._faq);
    if (this._faqPending) return this._faqPending;
    if (!this.faqUrl) return Promise.resolve(null);

    var self = this;

    try {
      var cached = window.sessionStorage.getItem(FAQ_CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.u === this.faqUrl && Array.isArray(parsed.d)) {
          this._faq = parsed.d;
          return Promise.resolve(this._faq);
        }
      }
    } catch (e) {
      /* Private mode or a corrupt entry. Fall through and fetch. */
    }

    this._faqPending = fetch(this.faqUrl, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('faq index ' + r.status);
        return r.json();
      })
      .then(function (data) {
        self._faq = data;
        try {
          window.sessionStorage.setItem(
            FAQ_CACHE_KEY,
            JSON.stringify({ u: self.faqUrl, d: data })
          );
        } catch (e) {
          /* Quota. The in-memory copy still serves this page view. */
        }
        return data;
      })
      .catch(function () {
        /* Answers are an enhancement. If the index cannot load, product search
           is untouched and the user sees no error -- there is nothing they
           could do about it. */
        self._faq = [];
        return self._faq;
      });

    return this._faqPending;
  };

  EverawSearchOverlay.prototype._clearAnswers = function () {
    if (this.answersBlock) this.answersBlock.setAttribute('hidden', '');
    if (this.answerList) this.answerList.textContent = '';
  };

  /* Full teardown: the query, Shopify's rendered results, and our answers.

     Closing used to hide the overlay and nothing else, so reopening restored
     the previous search verbatim -- stale results over a stale term. A search
     surface should open ready for a new question, not resume the last one.

     `focus` is opt-in because clearing on CLOSE must not summon the keyboard
     against a disappearing sheet. */
  EverawSearchOverlay.prototype._resetSearch = function (opts) {
    var focusAfter = opts && opts.focus;

    if (this.input) this.input.value = '';
    this.classList.remove('has-query');
    this._clearAnswers();

    var host = this.querySelector('predictive-search') || this.querySelector('search-form');
    if (host) {
      /* close(true) clears the term and the open/results attributes, but leaves
         the rendered markup behind; remove it or the old results flash on the
         next open before the new fetch lands. */
      if (typeof host.close === 'function') host.close(true);
      host.searchTerm = '';
      var rendered = host.querySelector('#predictive-search-results-groups-wrapper');
      if (rendered) rendered.remove();
      if (typeof host.toggleResetButton === 'function') host.toggleResetButton();
    }

    if (focusAfter && this.input) this.input.focus({ preventScroll: true });
  };

  EverawSearchOverlay.prototype._renderAnswers = function (query) {
    if (!this.answersBlock || !this.answerList) return;

    var tokens = contentTokens(tokenize(query));
    if (!tokens.length) {
      this.answersBlock.setAttribute('hidden', '');
      return;
    }

    var self = this;
    this._loadFaq().then(function (index) {
      if (!index || !index.length) return;
      /* The input may have moved on while the fetch was in flight. */
      if (tokenize(self.input.value).join(' ') !== tokens.join(' ')) return;

      var scored = [];
      for (var i = 0; i < index.length; i++) {
        var s = scoreEntry(index[i], tokens);
        if (s > 0) scored.push({ e: index[i], s: s });
      }
      scored.sort(function (a, b) {
        return b.s - a.s;
      });
      scored = scored.slice(0, FAQ_MAX_RESULTS);

      if (!scored.length) {
        self.answersBlock.setAttribute('hidden', '');
        return;
      }

      self.answerList.textContent = '';
      scored.forEach(function (hit) {
        var entry = hit.e;
        var node = document.createElement(entry.u ? 'a' : 'div');
        node.className = 'ew-search__answer';
        if (entry.u) node.href = entry.u;

        var q = document.createElement('span');
        q.className = 'ew-search__answer-q';
        highlightInto(q, entry.q, tokens);
        node.appendChild(q);

        var a = document.createElement('span');
        a.className = 'ew-search__answer-a';
        a.textContent = entry.a;
        node.appendChild(a);

        if (entry.u) {
          var more = document.createElement('span');
          more.className = 'ew-search__answer-more';
          more.textContent = 'Read more';
          node.appendChild(more);
        }
        self.answerList.appendChild(node);
      });
      self.answersBlock.removeAttribute('hidden');
    });
  };

  EverawSearchOverlay.prototype._bind = function () {
    var self = this;

    Array.prototype.forEach.call(document.querySelectorAll('[data-ew-search-open]'), function (btn) {
      /* Prefetch on pointerdown, not click. That is the whole optimisation:
         the fetch starts the moment the finger lands and runs in parallel with
         the overlay's 200ms entrance, so by the time the keyboard is up the
         index is already in memory. Cost to everyone who never taps: nothing. */
      btn.addEventListener(
        'pointerdown',
        function () {
          self._loadFaq();
        },
        { passive: true }
      );

      btn.addEventListener('click', function (event) {
        event.preventDefault();
        self.open(btn);
      });
    });

    Array.prototype.forEach.call(this.querySelectorAll('[data-ew-search-close]'), function (btn) {
      btn.addEventListener('click', function () {
        self.close();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
      }
    });

    this.input.addEventListener('input', function () {
      var q = self.input.value.trim();
      self.classList.toggle('has-query', q.length > 0);
      /* Match the network gate in predictive-search.js: below two characters
         nothing is worth showing. */
      if (q.length < 2) {
        if (self.answersBlock) self.answersBlock.setAttribute('hidden', '');
        return;
      }
      self._renderAnswers(q);
    });

    if (this.form) {
      this.form.addEventListener('submit', function () {
        pushRecent(self.input.value);
      });

      /* The reset button ("x") fires a `reset` event on the FORM, which
         SearchForm handles. It never fires `input`, so the answers listener
         below never ran and answers stayed on screen over an empty field --
         exactly what the V19 preview showed. */
      this.form.addEventListener('reset', function () {
        self._clearAnswers();
        self.classList.remove('has-query');
      });
    }

    Array.prototype.forEach.call(this.querySelectorAll('[data-ew-search-term]'), function (link) {
      link.addEventListener('click', function () {
        pushRecent(link.getAttribute('data-ew-search-term'));
      });
    });

    var clear = this.querySelector('[data-ew-search-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        writeRecent([]);
        self._renderRecent();
      });
    }
  };

  EverawSearchOverlay.prototype._renderRecent = function () {
    if (!this.recentBlock || !this.recentList) return;
    var list = readRecent();

    if (!list.length) {
      this.recentBlock.setAttribute('hidden', '');
      return;
    }

    this.recentBlock.removeAttribute('hidden');
    this.recentList.textContent = '';

    var searchRoot =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    list.forEach(function (term) {
      var a = document.createElement('a');
      a.className = 'ew-search__term';
      a.href =
        searchRoot + 'search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
      /* textContent, never innerHTML -- these strings came from user input and
         went through localStorage, so they are untrusted by the time we read
         them back. */
      a.textContent = term;
      this.recentList.appendChild(a);
    }, this);
  };

  EverawSearchOverlay.prototype.open = function (trigger) {
    if (this.isOpen) return;
    this.isOpen = true;
    this._trigger = trigger || null;

    this._renderRecent();
    this.removeAttribute('hidden');
    lockScroll();

    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');

    var self = this;
    /* Two frames: one for the browser to lay the element out now that it is no
       longer hidden, one for the transition to have a starting value to move
       from. Skipping this makes the overlay appear fully formed with no
       transition at all. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        self.classList.add('is-open');
        /* Focus AFTER the surface is on screen. Focusing a hidden input on iOS
           raises the keyboard against a blank screen. */
        self.input.focus({ preventScroll: true });
      });
    });
  };

  EverawSearchOverlay.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove('is-open');
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');

    /* Dismiss the keyboard before the surface leaves, otherwise iOS animates
       the keyboard down over an already-gone overlay. */
    this.input.blur();

    var self = this;
    var done = function () {
      self.setAttribute('hidden', '');
      unlockScroll();
      /* Clear AFTER the sheet is off screen, so the results are never seen
         emptying themselves on the way out. */
      self._resetSearch();
      if (self._trigger && self._trigger.focus) {
        self._trigger.focus({ preventScroll: true });
      }
      self._trigger = null;
    };

    if (G.prefersReducedMotion()) {
      done();
      return;
    }
    window.setTimeout(done, 200);
  };

  if (!window.customElements.get('everaw-search-overlay')) {
    window.customElements.define('everaw-search-overlay', EverawSearchOverlay);
  }

  /* ========================================================================
     ANSWERS ON THE SEARCH RESULTS PAGE
     ========================================================================
     Same index and same matcher as the overlay, rendered into the grouped
     results page. Shopify's index cannot see the FAQ content, so these can
     never arrive in search.results — the server has no way to know they exist.

     Runs only when the block is present (search template only) and only after
     load, so it costs nothing anywhere else.
     ======================================================================== */
  function renderResultsPageAnswers() {
    var block = document.querySelector('[data-ew-results-answers]');
    if (!block) return;

    var list = block.querySelector('[data-ew-results-answer-list]');
    var url = block.getAttribute('data-faq-index');
    var query = block.getAttribute('data-query') || '';
    if (!list || !url) return;

    var tokens = contentTokens(tokenize(query));
    if (!tokens.length) return;

    fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('faq index ' + r.status);
        return r.json();
      })
      .then(function (index) {
        var scored = [];
        for (var i = 0; i < index.length; i++) {
          var s = scoreEntry(index[i], tokens);
          if (s > 0) scored.push({ e: index[i], s: s });
        }
        scored.sort(function (a, b) {
          return b.s - a.s;
        });
        /* Four here rather than the overlay's three: this page has room, and a
           shopper who reached it is further into a question than someone still
           typing. */
        scored = scored.slice(0, 4);
        if (!scored.length) return;

        list.textContent = '';
        scored.forEach(function (hit) {
          var entry = hit.e;
          var node = document.createElement(entry.u ? 'a' : 'div');
          node.className = 'ew-search__answer';
          if (entry.u) node.href = entry.u;

          var q = document.createElement('span');
          q.className = 'ew-search__answer-q';
          highlightInto(q, entry.q, tokens);
          node.appendChild(q);

          var a = document.createElement('span');
          a.className = 'ew-search__answer-a';
          a.textContent = entry.a;
          node.appendChild(a);

          if (entry.u) {
            var more = document.createElement('span');
            more.className = 'ew-search__answer-more';
            more.textContent = 'Read more';
            node.appendChild(more);
          }
          list.appendChild(node);
        });
        block.removeAttribute('hidden');
      })
      .catch(function () {
        /* Enhancement only. Product results are untouched. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderResultsPageAnswers, { once: true });
  } else {
    renderResultsPageAnswers();
  }
})(window, document);

/* --------------------------------------------------------------------------
   Footer yield -- 2026-08-30.

   .ew-chat (bottom-left) and .stb-floating (bottom-right) are position:fixed,
   so they float above the footer and covered the payment-icon row and the
   copyright line respectively. Raising --z-footer is the wrong fix: it would
   put the footer above the launcher on every screen and break the drawer
   choreography at everaw-chrome.css:1236.

   TRIGGERING THIS CORRECTLY IS THE WHOLE PROBLEM.

   The footer is NOT a block at the end of the document. Its section wrapper is
   position:sticky; bottom:0 -- pinned to the viewport bottom the entire time,
   sitting UNDERNEATH an opaque #MainContent that slides up to uncover it
   (custom.css:3821). So .ct-main-footer's rect is top:0 at scrollY:0 and it
   intersects the viewport permanently. Observing the footer latches the yield
   on at page load and never releases it -- measured, on v22.5, before this.

   What actually correlates with "footer is showing" is #MainContent's BOTTOM
   edge rising off the viewport bottom. So we observe a zero-height sentinel
   appended to the end of #MainContent, with a fixed negative bottom rootMargin.
   The sentinel intersects exactly when the content's bottom edge is more than
   REVEAL_PX above the viewport bottom, which is when the footer is uncovered by
   that much. The margin is a constant, not derived from innerHeight, so it
   needs no recomputation on resize or rotate.

   The launcher gets its own custom properties rather than reusing
   --ew-chat-shift/-opacity/-scale, because the nav drawer's drag loop writes
   those every frame and would clobber the footer state 60x a second. The two
   compose in the transform; see everaw-chrome.css.
   -------------------------------------------------------------------------- */
(function (w, d) {
  'use strict';

  var REVEAL_PX = 160; /* launcher is ~64px at bottom:20px; badge 90px at 24px */
  var YIELD_CLASS = 'ew-footer-visible';
  var SHIFT = '120px';

  function init() {
    var main = d.getElementById('MainContent');
    if (!main) return;

    /* No IntersectionObserver means no yield: the elements behave exactly as
       they did before. Never leave them hidden on a fallback path. */
    if (typeof w.IntersectionObserver !== 'function') return;

    var root = d.documentElement;

    var sentinel = d.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:0;width:100%;margin:0;padding:0;pointer-events:none;';
    main.appendChild(sentinel);

    function apply(on) {
      root.classList.toggle(YIELD_CLASS, on);
      root.style.setProperty('--ew-chat-footer-shift', on ? SHIFT : '0px');
      root.style.setProperty('--ew-chat-footer-opacity', on ? '0' : '1');
    }

    apply(false);

    /* Deliberately NOT entries[i].isIntersecting.
       isIntersecting describes a BAND: true only while the sentinel is inside
       the (shrunk) root rect. Once the content scrolls far enough that the
       sentinel passes above the viewport top, isIntersecting goes false again
       -- which is precisely when the footer is MOST visible. Measured on v22.5:
       the yield released at the true bottom of the page, on both breakpoints.

       What we want is a THRESHOLD: has the content's bottom edge risen above
       the reveal line. entry.boundingClientRect is supplied on every callback,
       including the one where the sentinel leaves through the top, so compare
       against it directly and the answer stays correct past the crossing. */
    new w.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          apply(entries[i].boundingClientRect.top < w.innerHeight - REVEAL_PX);
        }
      },
      { rootMargin: '0px 0px -' + REVEAL_PX + 'px 0px', threshold: 0 }
    ).observe(sentinel);
  }

  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})(window, document);
